import { TARGET_CHANNEX } from '@core/config/rate-grid-features.config';
import type {
  RateGridBookingSummary,
  RateGridCommitResult,
  RateGridDifference,
  RateGridExcludedDate,
  RateGridHistoryEntry,
  RateGridHistoryPage,
  RateGridOperationChanges,
  RateGridOperationStatus,
  RateGridOperationType,
  RateGridPreflight,
  RateGridPreflightRequest,
  RateGridRead,
  RateGridSeason,
  RateGridWarning,
  SeasonWrite,
} from '@interfaces/rate-grid.interface';
import {
  addMonths,
  dateOf,
  datesBetween,
  daysInMonth,
  monthOf,
  todayInZone,
} from '@utils/rate-grid-date.util';

import { mockDb } from '../mock-db';
import {
  BASE_PRICE,
  CURRENCY,
  type MockDay,
  RateGridMockError,
  SEASON_PRICE,
  TARGET_WEB,
  TIME_ZONE,
  embeddedBookingOf,
  occupancyOf,
  ordinaryDay,
  resolveDay,
  targetAt,
  withRestrictions,
} from './rate-grid-mock.model';
import { SHOWCASE_BOOKINGS, SHOWCASE_THEMES, buildShowcaseMonth } from './rate-grid-showcase';

const SEED_RENTAL_IDS = new Set(['rental-mojacar-001', '00001']);
const SEED_MONTH = monthOf(todayInZone(TIME_ZONE));
const MONTH_PATTERN = /^\d{4}-\d{2}$/;
const ACTOR = mockDb.host.externalCodeName;

const PUBLISH_SETTLE_MS = 6000;

const PREFLIGHT_TTL_MS = 5 * 60 * 1000;

const SEASON_BAJA = { id: 'season-baja', name: 'Temporada baja', colour: '#838b9c' };

const FAILING_DATES = new Set<string>();

const BOOKINGS = new Map<string, RateGridBookingSummary>();

function seedBooking(
  id: string,
  reference: string,
  guest: string,
  channel: string,
  bucket: RateGridBookingSummary['bucket'],
  checkIn: string,
  checkOut: string,
  nights: number
): RateGridBookingSummary {
  const summary: RateGridBookingSummary = {
    rentalId: '',
    bookingId: id,
    reference,
    guestDisplayName: guest,
    checkIn,
    checkOut,
    bucket,
    substate: bucket === 'PENDING' ? 'AWAITING_PAYMENT' : null,
    bookingStatus:
      bucket === 'CONFIRMED'
        ? 'CHECKIN_SCHEDULED'
        : bucket === 'ACTIVE'
          ? 'CHECKOUT_PENDING'
          : bucket === 'FINISHED'
            ? 'FINISHED'
            : 'ACCEPTANCE_PENDING',
    channel,
    total: { amount: BASE_PRICE * nights, currency: CURRENCY },
    navigationPath: null,
  };
  BOOKINGS.set(id, summary);
  return summary;
}

/**
 * Spreads the seed events over fixed fractions of the month so they never collide whatever the
 * month length, and anchors the active booking across today so every cell state stays reachable.
 */
function seedAnchors(total: number): {
  pastFrom: number;
  activeFrom: number;
  futureFrom: number;
  manual: number;
  channelSync: number;
  blocked: number;
  pendingSync: number;
  failedSync: number;
  closedArrival: number;
  seasonFrom: number;
} {
  const at = (fraction: number) => Math.max(1, Math.min(total, Math.round(total * fraction)));
  const today = Number(todayInZone(TIME_ZONE).slice(8, 10));
  // The stay spanning today is placed first and the other two are kept clear of it: a rental cannot
  // hold two bookings on one night, and when they did collide the later one overwrote the earlier's
  // nights and left its checkout day stranded with no stay behind it.
  const activeFrom = Math.max(1, Math.min(total - 1, today - 1));
  const activeEnd = activeFrom + 2;
  return {
    activeFrom,
    pastFrom: Math.max(1, Math.min(at(0.1), activeFrom - 2)),
    futureFrom: Math.min(total - 1, Math.max(at(0.65), activeEnd + 1)),
    manual: at(0.26),
    channelSync: at(0.33),
    blocked: at(0.52),
    pendingSync: at(0.58),
    failedSync: at(0.39),
    closedArrival: at(0.71),
    seasonFrom: at(0.78),
  };
}

/**
 * Builds the seed month for the demo rentals: a finished booking, one spanning today, a future one,
 * a manual price, a channel-synced price, a night still publishing, one the web refused, a closure
 * and a season tail. Anchored to today, so every cell state the calendar draws is reachable without
 * editing anything first.
 */
function buildSeedDays(): MockDay[] {
  const total = daysInMonth(SEED_MONTH);
  const anchors = seedAnchors(total);
  const byDay = new Map<number, MockDay>();

  for (let day = 1; day <= total; day += 1) {
    const date = dateOf(SEED_MONTH, day);
    const inSeason = day >= anchors.seasonFrom;
    byDay.set(
      day,
      ordinaryDay(date, inSeason ? SEASON_PRICE : BASE_PRICE, inSeason ? 'SEASON' : 'BASE')
    );
  }

  const stayAt = (
    from: number,
    nights: number,
    id: string,
    reference: string,
    guest: string,
    channel: string,
    bucket: RateGridBookingSummary['bucket'],
    states: Parameters<typeof embeddedBookingOf>[1] = {}
  ) => {
    const summary = seedBooking(
      id,
      reference,
      guest,
      channel,
      bucket,
      dateOf(SEED_MONTH, from),
      dateOf(SEED_MONTH, Math.min(from + nights, total)),
      nights
    );
    // As the backend reads it: the night carries the channel and the booking summary embedded.
    const occupancy = occupancyOf(
      id,
      reference,
      guest,
      bucket,
      channel,
      embeddedBookingOf(summary, states)
    );
    for (let day = from; day < from + nights; day += 1) {
      const entry = byDay.get(day);
      if (entry) {
        byDay.set(day, { ...entry, occupancy: { ...occupancy } });
      }
    }
    return summary;
  };

  stayAt(
    anchors.activeFrom,
    2,
    'booking-rategrid-02',
    'RSD-260611',
    'Marco Rossi',
    'Airbnb',
    'ACTIVE',
    // The guest is inside with the payment still pending: the band shows the warning.
    { paymentState: 'PAYMENT_PENDING' }
  );
  stayAt(
    anchors.pastFrom,
    1,
    'booking-rategrid-01',
    'RSD-260602',
    'Laura Fernández',
    'Booking.com',
    'FINISHED'
  );
  stayAt(
    anchors.futureFrom,
    2,
    'booking-rategrid-03',
    'RSD-260620',
    'Sophie Dubois',
    'Web directa',
    'CONFIRMED'
  );

  const taken = new Set<number>();

  const freeDay = (preferred: number): number | null => {
    for (let offset = 0; offset < total; offset += 1) {
      for (const day of [preferred + offset, preferred - offset]) {
        const entry = byDay.get(day);
        if (entry?.occupancy.status === 'FREE' && !taken.has(day)) {
          taken.add(day);
          return day;
        }
      }
    }
    return null;
  };

  const apply = (preferred: number, patch: (entry: MockDay) => MockDay) => {
    const day = freeDay(preferred);
    if (day === null) {
      return;
    }
    byDay.set(day, patch(byDay.get(day)!));
  };

  apply(anchors.manual, entry => ({
    ...entry,
    price: { amount: 350, currency: CURRENCY, source: 'MANUAL' },
    lastChange: { type: 'PRICE', at: `${entry.date}T09:15:00Z`, actor: ACTOR },
  }));

  apply(anchors.channelSync, entry => ({
    ...entry,
    price: { amount: 280, currency: CURRENCY, source: 'CHANNEL_SYNC' },
    lastChange: { type: 'PRICE', at: `${entry.date}T03:00:00Z`, actor: 'channex-sync' },
  }));

  apply(anchors.closedArrival, entry =>
    withRestrictions(
      {
        ...entry,
        lastChange: { type: 'RESTRICTIONS', at: `${entry.date}T16:05:00Z`, actor: ACTOR },
      },
      { cta: true, ctd: true }
    )
  );

  apply(anchors.pendingSync, entry => ({
    ...entry,
    price: { amount: 310, currency: CURRENCY, source: 'MANUAL' },
    targets: [targetAt(TARGET_WEB, 'PENDING', new Date().toISOString(), 'op-seed-pending')],
    lastChange: { type: 'PRICE', at: `${entry.date}T14:20:00Z`, actor: ACTOR },
  }));

  apply(anchors.failedSync, entry => {
    FAILING_DATES.add(entry.date);
    return {
      ...entry,
      price: { amount: 295, currency: CURRENCY, source: 'MANUAL' },
      targets: [targetAt(TARGET_WEB, 'FAILED', `${entry.date}T09:30:00Z`, 'op-seed-failed', true)],
      lastChange: { type: 'PRICE', at: `${entry.date}T09:20:00Z`, actor: ACTOR },
    };
  });

  apply(anchors.blocked, entry => ({
    ...entry,
    manualClosure: { closed: true, reason: 'Mantenimiento' },
    lastChange: { type: 'MANUAL_CLOSURE', at: `${entry.date}T08:00:00Z`, actor: ACTOR },
  }));

  return Array.from(byDay.values());
}

const SEED_DAYS: MockDay[] = buildSeedDays();

/**
 * The change log of the seed month, read back off the nights the seed actually marked rather than
 * written out by hand: the anchors shift with the month's length and around its bookings, so a fixed
 * list described days that carried none of those marks.
 */
function buildSeedHistory(days: readonly MockDay[]): RateGridHistoryEntry[] {
  const find = (match: (day: MockDay) => boolean) => days.find(match) ?? null;
  const entries: RateGridHistoryEntry[] = [];

  const entry = (
    id: string,
    type: RateGridHistoryEntry['type'],
    day: MockDay,
    time: string,
    differences: RateGridHistoryEntry['differences'],
    extra: Partial<RateGridHistoryEntry> = {}
  ): RateGridHistoryEntry => ({
    id,
    operationId: null,
    type,
    actor: ACTOR,
    changedAt: `${day.date}T${time}`,
    rangeFrom: day.date,
    rangeTo: day.date,
    source: null,
    target: null,
    reason: null,
    correlationId: null,
    differences,
    ...extra,
  });

  const manual = find(day => day.price.source === 'MANUAL' && day.targets[0]?.status !== 'FAILED');
  if (manual) {
    entries.push(
      entry('history-rategrid-01', 'PRICE', manual, '09:15:00Z', [
        {
          date: manual.date,
          field: 'PRICE',
          oldValue: String(BASE_PRICE),
          newValue: String(manual.price.amount),
        },
      ])
    );
  }

  const synced = find(day => day.price.source === 'CHANNEL_SYNC');
  if (synced) {
    entries.push(
      entry(
        'history-rategrid-02',
        'PRICE',
        synced,
        '03:00:00Z',
        [
          {
            date: synced.date,
            field: 'PRICE',
            oldValue: String(BASE_PRICE),
            newValue: String(synced.price.amount),
          },
        ],
        { actor: 'channex-sync', source: 'CHANNEL' }
      )
    );
  }

  const closed = find(day => day.manualClosure.closed);
  if (closed) {
    entries.push(
      entry(
        'history-rategrid-03',
        'MANUAL_CLOSURE',
        closed,
        '08:00:00Z',
        [{ date: closed.date, field: 'MANUAL_CLOSED', oldValue: 'false', newValue: 'true' }],
        { reason: closed.manualClosure.reason }
      )
    );
  }

  const restricted = find(day => day.restrictions.overrides.cta === true);
  if (restricted) {
    entries.push(
      entry('history-rategrid-04', 'RESTRICTIONS', restricted, '16:05:00Z', [
        { date: restricted.date, field: 'CTA', oldValue: 'false', newValue: 'true' },
        { date: restricted.date, field: 'CTD', oldValue: 'false', newValue: 'true' },
      ])
    );
  }

  const failed = find(day => day.targets.some(target => target.status === 'FAILED'));
  if (failed) {
    entries.push(
      entry('history-rategrid-05', 'WEB_PUBLICATION', failed, '09:30:00Z', [], {
        target: TARGET_WEB,
        operationId: 'op-seed-failed',
      })
    );
  }

  return entries;
}

const SEED_HISTORY: RateGridHistoryEntry[] = buildSeedHistory(SEED_DAYS);

/**
 * Builds the days of an ordinary month, priced flat at base, for any rental/month outside the frozen
 * seed. A month that is not `YYYY-MM` yields no days rather than malformed dates.
 */
function buildGenericDays(month: string): MockDay[] {
  const days: MockDay[] = [];
  const total = MONTH_PATTERN.test(month) ? daysInMonth(month) : 0;
  for (let day = 1; day <= total; day += 1) {
    days.push(ordinaryDay(dateOf(month, day), BASE_PRICE));
  }
  return days;
}

const MONTHS = new Map<string, MockDay[]>();

const HISTORIES = new Map<string, RateGridHistoryEntry[]>();

/**
 * Materialises the days of one rental-month once and keeps them, so a write mutates the very objects
 * the next read serves. The frozen seed backs the current month of the seed rentals; everything else
 * starts as a flat ordinary month.
 */
function daysOf(rentalId: string, month: string): MockDay[] {
  const key = `${rentalId}|${month}`;
  const stored = MONTHS.get(key);
  if (stored) {
    return stored;
  }
  MONTHS.set(key, buildMonth(rentalId, month));
  return MONTHS.get(key)!;
}

/**
 * The nights a month starts with. The seed rentals get the frozen seed for the current month and a
 * themed month for each of the four after it, so every case the calendar draws is one or two clicks
 * from today rather than something the host has to produce by editing.
 */
function buildMonth(rentalId: string, month: string): MockDay[] {
  if (!SEED_RENTAL_IDS.has(rentalId)) {
    return buildGenericDays(month);
  }
  if (month === SEED_MONTH) {
    return SEED_DAYS;
  }
  const offset = SHOWCASE_THEMES.findIndex(
    (_, index) => addMonths(SEED_MONTH, index + 1) === month
  );
  if (offset === -1) {
    return buildGenericDays(month);
  }
  return buildShowcaseMonth(SHOWCASE_THEMES[offset], buildGenericDays(month), {
    month,
    season: SEASON_BAJA,
    seasonPrice: SEASON_PRICE,
    actor: ACTOR,
  });
}

/** Materialises a rental's history once; only the seed rentals start with entries. */
function historyOf(rentalId: string): RateGridHistoryEntry[] {
  const stored = HISTORIES.get(rentalId);
  if (stored) {
    return stored;
  }
  const history = SEED_RENTAL_IDS.has(rentalId) ? structuredClone(SEED_HISTORY) : [];
  HISTORIES.set(rentalId, history);
  return history;
}

/**
 * Moves a night's destinations on from PENDING once they have had long enough to answer. One
 * nominated night fails instead of publishing, and is marked retryable, so the manual retry has
 * a real case to act on. Mutates the stored day, so the outcome sticks.
 */
function settlePublications(days: MockDay[]): void {
  const settled = Date.now() - PUBLISH_SETTLE_MS;
  for (const day of days) {
    day.targets = day.targets.map(target => {
      if (target.status !== 'PENDING' || target.updatedAt === null) {
        return target;
      }
      if (Date.parse(target.updatedAt) > settled) {
        return target;
      }
      return FAILING_DATES.has(day.date)
        ? { ...target, status: 'FAILED' as const, retryAllowed: true }
        : { ...target, status: 'PUBLISHED' as const, updatedAt: new Date().toISOString() };
    });
  }
}

/**
 * Prices the nights a season covers with the rule in force for it, so overriding a season repaints
 * its dates in the grid. A night the host priced by hand keeps that price: a manual edit outranks the
 * season it sits in, which is the same precedence the panel shows as MANUAL over SEASON.
 */
function applySeasons(rentalId: string, days: MockDay[]): MockDay[] {
  const seasons = seasonsOf(rentalId);
  if (seasons.length === 0) {
    return days;
  }
  return days.map(day => {
    const season = seasons.find(entry => day.date >= entry.from && day.date <= entry.to);
    if (!season) {
      return day;
    }
    const seasonRef = { id: season.id, name: season.name, colour: season.colour };
    if (day.price.source === 'MANUAL') {
      return { ...day, season: seasonRef };
    }
    const applied = APPLIED_OVERRIDES.get(`${rentalId}:${season.id}`) ?? season.override;
    const price = applied.price ?? season.host.price;
    const minStay = applied.minStay ?? season.host.minStay;
    const maxStay = applied.maxStay ?? season.host.maxStay;
    return {
      ...day,
      season: seasonRef,
      price: {
        amount: price ?? day.price.amount,
        currency: CURRENCY,
        source: 'SEASON',
      },
      restrictions: {
        overrides: day.restrictions.overrides,
        effective: {
          minStay:
            day.restrictions.overrides.minStay ?? minStay ?? day.restrictions.effective.minStay,
          maxStay:
            day.restrictions.overrides.maxStay ?? maxStay ?? day.restrictions.effective.maxStay,
          cta: day.restrictions.effective.cta,
          ctd: day.restrictions.effective.ctd,
        },
      },
    };
  });
}

/**
 * The nights between `from` and `to` the rate grid mock already holds with a manual price for the
 * rental, keyed by date with that price. Only months it has built are read, so asking never seeds
 * new ones as a side effect.
 */
export function manualRateGridNightsMock(
  rentalId: string,
  from: string,
  to: string
): Map<string, number | null> {
  const nights = new Map<string, number | null>();
  for (const [key, days] of MONTHS) {
    if (!key.startsWith(`${rentalId}|`)) {
      continue;
    }
    for (const day of days) {
      if (day.price.source === 'MANUAL' && day.date >= from && day.date <= to) {
        nights.set(day.date, day.price.amount);
      }
    }
  }
  return nights;
}

export const MAX_RANGE_NIGHTS = 180;

/** Every YYYY-MM month from `first` to `last` inclusive, so a read spanning three months misses none. */
function monthsBetween(first: string, last: string): string[] {
  if (!MONTH_PATTERN.test(first) || !MONTH_PATTERN.test(last)) {
    return [first, last];
  }
  const months: string[] = [];
  for (let month = first; month <= last; month = addMonths(month, 1)) {
    months.push(month);
  }
  return months;
}

/** Mock: GET /rentals/{rentalId}/rate-grid?from&to. */
export function getRateGridRangeMock(rentalId: string, from: string, to: string): RateGridRead {
  const today = todayInZone(TIME_ZONE);
  const months = monthsBetween(monthOf(from), monthOf(to));
  const days: MockDay[] = [];
  for (const month of months) {
    const stored = daysOf(rentalId, month);
    settlePublications(stored);
    days.push(...applySeasons(rentalId, stored));
  }
  const span = days
    .filter(day => day.date >= from && day.date <= to)
    .sort((left, right) => left.date.localeCompare(right.date))
    .map(day => resolveDay(day, today));

  return {
    rental: {
      id: rentalId,
      title: mockDb.host.externalCodeName,
      timeZone: TIME_ZONE,
      currency: CURRENCY,
      defaultPrice: BASE_PRICE,
    },
    range: { from, to, today },
    permissions: { read: true, edit: true, history: true },
    capabilities: {
      // Channex is declared visible, as a backend with the integration live would declare it, and so
      // is a destination this build has never heard of, which `targetVisible` drops rather than the
      // mock.
      targets: {
        [TARGET_WEB]: { visible: true, fields: { price: 'WRITE', availability: 'WRITE' } },
        [TARGET_CHANNEX]: {
          visible: SEED_RENTAL_IDS.has(rentalId),
          fields: { price: 'WRITE', availability: 'WRITE' },
        },
        FUTURE_CHANNEL: { visible: true, fields: { price: 'READ' } },
      },
    },
    days: span as RateGridRead['days'],
  };
}

/** Mock: GET /rentals/{rentalId}/rate-grid/history?page&size. */
export function getRateGridHistoryMock(rentalId: string, page = 0, size = 20): RateGridHistoryPage {
  const items = historyOf(rentalId);
  const start = page * size;
  return {
    rentalId,
    page,
    size,
    totalElements: items.length,
    totalPages: Math.max(1, Math.ceil(items.length / size)),
    items: items.slice(start, start + size),
  };
}

/** Mock: GET /rentals/{rentalId}/rate-grid/days/{date}/booking-summary. */
export function getRateGridBookingSummaryMock(
  rentalId: string,
  date: string
): RateGridBookingSummary {
  const day = dayAt(rentalId, date);
  const bookingId = day?.occupancy.bookingId ?? null;
  const summary = bookingId
    ? (BOOKINGS.get(bookingId) ?? SHOWCASE_BOOKINGS.get(bookingId))
    : undefined;
  if (!summary) {
    throw new RateGridMockError(404, 'RATE_GRID_OPERATION_NOT_FOUND');
  }
  return { ...summary, rentalId };
}

let counter = 0;

function nextId(prefix: string): string {
  counter += 1;
  return `${prefix}-${counter}`;
}

/** Finds a materialised day by date so writes mutate the served month instead of a copy. */
function dayAt(rentalId: string, date: string): MockDay | undefined {
  return daysOf(rentalId, monthOf(date)).find(day => day.date === date);
}

interface StoredPreflight {
  rentalId: string;
  request: RateGridPreflightRequest;
  result: RateGridPreflight;
  versions: Record<string, number>;
}

const PREFLIGHTS = new Map<string, StoredPreflight>();

const COMMITS = new Map<string, RateGridCommitResult>();

const UNCERTAIN_COMMIT_PRICE = 999;
const REPLAY_CONFLICT_PRICE = 998;

const REPLAY_CONFLICTS = new Set<string>();

const OPERATIONS = new Map<string, RateGridOperationStatus>();

const WRITES_THROUGH: ReadonlySet<RateGridOperationType> = new Set([
  'SET_PRICE',
  'SET_RESTRICTIONS',
  'SET_CHANNEL_PRICES',
  'RESET_PRICE',
]);

/**
 * Why a night cannot take a given write, in the same order the server decides it. A block or an
 * unblock leaves booked nights out; a price or a stay is written to them, and only leaves out a
 * night that already holds the value.
 */
function exclusionOf(
  day: MockDay | undefined,
  operation: RateGridOperationType,
  changes: RateGridOperationChanges
): string | null {
  if (!day) {
    return 'OUT_OF_RANGE';
  }
  if (!WRITES_THROUGH.has(operation) && day.occupancy.status === 'OCCUPIED') {
    return 'OCCUPIED';
  }
  if (operation === 'CLOSE' && day.manualClosure.closed) {
    return 'NO_CHANGE';
  }
  if (operation === 'REOPEN' && !day.manualClosure.closed) {
    return 'NO_CHANGE';
  }
  if (
    operation === 'SET_PRICE' &&
    day.price.source === 'MANUAL' &&
    day.price.amount === changes.price
  ) {
    return 'NO_CHANGE';
  }
  if (operation === 'SET_RESTRICTIONS') {
    const { before, after } = differenceOf(day, operation, changes);
    if (JSON.stringify(before) === JSON.stringify(after)) {
      return 'NO_CHANGE';
    }
  }
  return null;
}

/** The server's warnings for a night a price or stay is written to anyway. */
function warningsOf(day: MockDay, operation: RateGridOperationType): string[] {
  if (!WRITES_THROUGH.has(operation)) {
    return [];
  }
  const subject = operation === 'SET_RESTRICTIONS' ? 'RESTRICTIONS' : 'PRICE';
  return [
    ...(day.occupancy.status === 'OCCUPIED' ? [`OCCUPIED_${subject}_WILL_NOT_CHANGE_BOOKING`] : []),
    ...(day.manualClosure.closed ? [`MANUALLY_CLOSED_${subject}_PREPARED`] : []),
  ];
}

/** What one night would look like after a write, as the `before`/`after` pair of the diff. */
function differenceOf(
  day: MockDay,
  operation: RateGridOperationType,
  changes: RateGridOperationChanges
) {
  switch (operation) {
    case 'SET_PRICE':
      return {
        before: { price: day.price.amount },
        after: { price: changes.price ?? day.price.amount },
      };
    case 'CLOSE':
      return { before: { closed: false }, after: { closed: true, reason: changes.reason ?? null } };
    case 'REOPEN':
      return { before: { closed: true }, after: { closed: false } };
    case 'SET_RESTRICTIONS':
      return {
        before: { ...day.restrictions.effective },
        after: {
          minStay:
            changes.minStay === undefined ? day.restrictions.effective.minStay : changes.minStay,
          maxStay:
            changes.maxStay === undefined ? day.restrictions.effective.maxStay : changes.maxStay,
          cta: changes.cta === undefined ? day.restrictions.effective.cta : changes.cta,
          ctd: changes.ctd === undefined ? day.restrictions.effective.ctd : changes.ctd,
        },
      };
    case 'SET_CHANNEL_PRICES':
      return {
        before: { price: day.price.amount },
        after: { prices: changes.prices ?? {} },
      };
    case 'RESET_PRICE':
      return { before: { price: day.price.amount }, after: { price: null } };
    case 'SET_CHANNEL_AVAILABILITY':
      return { before: { channels: null }, after: { channels: changes.channels ?? [] } };
  }
}

/**
 * Mock: POST /rentals/{rentalId}/rate-grid/operations/preflight. Answers with the exact write set
 * the commit would apply — which is what the confirmation shows — and remembers the versions it saw
 * so a commit sent after someone else's write can be refused as stale.
 */
export function preflightRateGridMock(
  rentalId: string,
  body: RateGridPreflightRequest
): RateGridPreflight {
  const today = todayInZone(TIME_ZONE);
  const dates = datesBetween(body.range.from, body.range.to);
  if (dates.length === 0) {
    throw new RateGridMockError(422, 'RATE_GRID_NO_EFFECTIVE_DATES');
  }
  if (body.operation === 'SET_PRICE' && (body.changes.price ?? 0) <= 0) {
    throw new RateGridMockError(422, 'RATE_GRID_INVALID_PRICE');
  }
  if (body.range.from < today) {
    throw new RateGridMockError(422, 'RATE_GRID_PAST_DATE');
  }

  const effectiveDates: string[] = [];
  const excludedDates: RateGridExcludedDate[] = [];
  const warnings: RateGridWarning[] = [];
  const diff: RateGridDifference[] = [];
  const versions: Record<string, number> = {};

  for (const date of dates) {
    const day = dayAt(rentalId, date);
    const reason = exclusionOf(day, body.operation, body.changes);
    if (reason !== null || !day) {
      excludedDates.push({ date, reason: reason ?? 'OUT_OF_RANGE' });
      continue;
    }
    effectiveDates.push(date);
    versions[date] = day.version;
    warnings.push(...warningsOf(day, body.operation).map(code => ({ date, code })));
    diff.push({ date, version: day.version, ...differenceOf(day, body.operation, body.changes) });
  }

  const result: RateGridPreflight = {
    preflightId: nextId('preflight'),
    rentalId,
    expiresAt: new Date(Date.now() + PREFLIGHT_TTL_MS).toISOString(),
    operation: body.operation,
    currency: CURRENCY,
    effectiveDates,
    excludedDates,
    warnings,
    diff,
  };

  PREFLIGHTS.set(result.preflightId, { rentalId, request: body, result, versions });
  return result;
}

/** Applies one confirmed change to a night, bumping its version and sending it out again. */
function applyChange(
  day: MockDay,
  operation: RateGridOperationType,
  changes: RateGridOperationChanges,
  operationId: string
): void {
  const now = new Date().toISOString();
  day.version += 1;

  if (operation === 'SET_PRICE' && changes.price !== undefined) {
    day.price = { amount: changes.price, currency: CURRENCY, source: 'MANUAL' };
    day.lastChange = { type: 'PRICE', at: now, actor: ACTOR };
  }
  if (operation === 'RESET_PRICE') {
    day.price = { amount: null, currency: CURRENCY, source: 'BASE' };
    day.lastChange = { type: 'PRICE', at: now, actor: ACTOR };
  }
  if (operation === 'CLOSE') {
    day.manualClosure = { closed: true, reason: changes.reason ?? null };
    day.lastChange = { type: 'MANUAL_CLOSURE', at: now, actor: ACTOR };
  }
  if (operation === 'REOPEN') {
    day.manualClosure = { closed: false, reason: null };
    day.lastChange = { type: 'REOPEN', at: now, actor: ACTOR };
  }
  if (operation === 'SET_RESTRICTIONS') {
    const next = withRestrictions(day, {
      ...(changes.minStay === undefined ? {} : { minStay: changes.minStay }),
      ...(changes.maxStay === undefined ? {} : { maxStay: changes.maxStay }),
      ...(changes.cta === undefined ? {} : { cta: changes.cta }),
      ...(changes.ctd === undefined ? {} : { ctd: changes.ctd }),
    });
    day.restrictions = next.restrictions;
    day.lastChange = { type: 'RESTRICTIONS', at: now, actor: ACTOR };
  }

  // Both destinations go out on every write: the web settles on the mock's own clock, Channex is
  // left pending because nothing here can speak for it.
  day.targets = [
    targetAt(TARGET_WEB, 'PENDING', now, operationId),
    targetAt(TARGET_CHANNEX, 'PENDING', null, operationId),
  ];
}

const HISTORY_TYPE_OF: Record<RateGridOperationType, RateGridHistoryEntry['type']> = {
  SET_PRICE: 'PRICE',
  CLOSE: 'MANUAL_CLOSURE',
  REOPEN: 'REOPEN',
  SET_RESTRICTIONS: 'RESTRICTIONS',
  SET_CHANNEL_PRICES: 'PRICE',
  RESET_PRICE: 'PRICE',
  SET_CHANNEL_AVAILABILITY: 'OTHER',
};

/**
 * Mock: POST /rentals/{rentalId}/rate-grid/operations. The idempotency key is the identity of the
 * confirmation: sending it twice returns the operation that already landed instead of writing again,
 * which is exactly what makes a retry after a timeout safe.
 */
export function commitRateGridMock(
  rentalId: string,
  preflightId: string,
  idempotencyKey: string
): RateGridCommitResult {
  if (!idempotencyKey) {
    throw new RateGridMockError(422, 'RATE_GRID_IDEMPOTENCY_KEY_REQUIRED');
  }
  const replayed = COMMITS.get(idempotencyKey);
  if (replayed) {
    if (REPLAY_CONFLICTS.delete(idempotencyKey)) {
      throw new RateGridMockError(409, 'RATE_GRID_IDEMPOTENCY_CONFLICT');
    }
    return replayed;
  }

  const stored = PREFLIGHTS.get(preflightId);
  // Backend answers both an unknown preflight and an expired one with the same 409: the quoted
  // write set no longer describes the grid, and the panel re-reads either way.
  if (stored?.rentalId !== rentalId) {
    throw new RateGridMockError(409, 'RATE_GRID_STALE_VERSION');
  }
  if (Date.parse(stored.result.expiresAt) < Date.now()) {
    throw new RateGridMockError(409, 'RATE_GRID_STALE_VERSION');
  }
  if (stored.result.effectiveDates.length === 0) {
    throw new RateGridMockError(422, 'RATE_GRID_NO_EFFECTIVE_DATES');
  }

  const operationId = nextId('operation');
  const correlationId = nextId('correlation');
  const changedDays: { date: string; version: number }[] = [];

  for (const date of stored.result.effectiveDates) {
    const day = dayAt(rentalId, date);
    if (!day) {
      throw new RateGridMockError(409, 'RATE_GRID_STALE_VERSION');
    }
    if (day.version !== stored.versions[date]) {
      throw new RateGridMockError(409, 'RATE_GRID_STALE_VERSION');
    }
  }

  for (const date of stored.result.effectiveDates) {
    const day = dayAt(rentalId, date)!;
    applyChange(day, stored.result.operation, stored.request.changes, operationId);
    changedDays.push({ date, version: day.version });
  }

  historyOf(rentalId).unshift({
    id: nextId('history'),
    operationId,
    type: HISTORY_TYPE_OF[stored.result.operation],
    actor: ACTOR,
    changedAt: new Date().toISOString(),
    rangeFrom: stored.result.effectiveDates[0],
    rangeTo: stored.result.effectiveDates[stored.result.effectiveDates.length - 1],
    source: 'BO',
    target: null,
    reason: stored.request.changes.reason ?? null,
    correlationId,
    differences: stored.result.diff.flatMap(entry =>
      Object.keys(entry.after).map(field => ({
        date: entry.date,
        field,
        oldValue: formatValue(entry.before[field]),
        newValue: formatValue(entry.after[field]),
      }))
    ),
  });

  const result: RateGridCommitResult = {
    operationId,
    rentalId,
    correlationId,
    changedDays,
    targets: [
      { target: TARGET_WEB, status: 'PENDING' },
      { target: TARGET_CHANNEX, status: 'PENDING' },
    ],
  };

  COMMITS.set(idempotencyKey, result);
  PREFLIGHTS.delete(preflightId);
  OPERATIONS.set(operationId, {
    operationId,
    rentalId,
    operationType: stored.result.operation,
    operationStatus: 'COMPLETED',
    correlationId,
    from: result.changedDays[0].date,
    to: result.changedDays[result.changedDays.length - 1].date,
    changedDays,
    targets: [
      {
        target: TARGET_WEB,
        status: 'PENDING',
        lastAttemptAt: null,
        publishedAt: null,
        error: null,
        retryAllowed: false,
      },
      {
        target: TARGET_CHANNEX,
        status: 'PENDING',
        lastAttemptAt: null,
        publishedAt: null,
        error: null,
        retryAllowed: false,
      },
    ],
  });

  // The write is done and recorded before this throws, which is exactly what makes the outcome
  // uncertain rather than refused: only repeating under the same key can tell the host which it was.
  const price = stored.request.changes.price;
  if (price === REPLAY_CONFLICT_PRICE) {
    REPLAY_CONFLICTS.add(idempotencyKey);
  }
  if (price === UNCERTAIN_COMMIT_PRICE || price === REPLAY_CONFLICT_PRICE) {
    throw new RateGridMockError(500, 'RATE_GRID_INTERNAL_ERROR');
  }

  return result;
}

/** A diff value as the history carries it: every field's before/after is sent as text. */
function formatValue(value: unknown): string | null {
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return typeof value === 'string' ? value : null;
}

/** Mock: GET /rentals/{rentalId}/rate-grid/operations/{operationId}. */
export function getRateGridOperationMock(
  rentalId: string,
  operationId: string
): RateGridOperationStatus {
  const operation = OPERATIONS.get(operationId);
  if (operation?.rentalId !== rentalId) {
    throw new RateGridMockError(404, 'RATE_GRID_OPERATION_NOT_FOUND');
  }
  return operation;
}

/** Puts the web destination back to pending, leaving every other destination as it stood. */
function republishWeb(day: MockDay, now: string, operationId: string): void {
  day.targets = [
    targetAt(TARGET_WEB, 'PENDING', now, operationId),
    ...day.targets.filter(target => target.target !== TARGET_WEB),
  ];
}

/**
 * Mock: POST /rentals/{rentalId}/rate-grid/operations/{operationId}/retry. The business change is
 * already saved, so this only starts one more publication cycle: the operation stays COMPLETED, its
 * versions are untouched, and only the web destination moves back to PENDING.
 */
export function retryRateGridPublicationMock(
  rentalId: string,
  operationId: string,
  idempotencyKey: string
): RateGridOperationStatus {
  if (!idempotencyKey) {
    throw new RateGridMockError(422, 'RATE_GRID_IDEMPOTENCY_KEY_REQUIRED');
  }

  const now = new Date().toISOString();
  const operation = OPERATIONS.get(operationId);

  // The seeded failures carry an operation the mock never issued, so the retry answers for them from
  // the nights themselves — otherwise the one case the button exists for could never be pressed.
  if (!operation) {
    const dates = retryableSeedDates(rentalId, operationId);
    if (dates.length === 0) {
      throw new RateGridMockError(404, 'RATE_GRID_OPERATION_NOT_FOUND');
    }
    for (const date of dates) {
      FAILING_DATES.delete(date);
      const day = dayAt(rentalId, date)!;
      republishWeb(day, now, operationId);
    }
    return {
      operationId,
      rentalId,
      operationType: 'SET_PRICE',
      operationStatus: 'COMPLETED',
      correlationId: null,
      from: dates[0],
      to: dates[dates.length - 1],
      changedDays: dates.map(date => ({ date, version: dayAt(rentalId, date)!.version })),
      targets: [
        {
          target: TARGET_WEB,
          status: 'PENDING',
          lastAttemptAt: null,
          publishedAt: null,
          error: null,
          retryAllowed: false,
        },
      ],
    };
  }

  if (operation.rentalId !== rentalId) {
    throw new RateGridMockError(404, 'RATE_GRID_OPERATION_NOT_FOUND');
  }
  const web = operation.targets.find(target => target.target === TARGET_WEB);
  if (!web?.retryAllowed) {
    throw new RateGridMockError(409, 'RATE_GRID_PUBLICATION_RETRY_NOT_ALLOWED');
  }

  for (const changed of operation.changedDays) {
    FAILING_DATES.delete(changed.date);
    const day = dayAt(rentalId, changed.date);
    if (day) {
      republishWeb(day, now, operationId);
    }
  }

  operation.targets = operation.targets.map(target =>
    target.target === TARGET_WEB
      ? { ...target, status: 'PENDING' as const, error: null, retryAllowed: false }
      : target
  );
  return operation;
}

/** The seeded nights a given operation id failed on, so their retry can be honoured. */
function retryableSeedDates(rentalId: string, operationId: string): string[] {
  const days = daysOf(rentalId, SEED_MONTH);
  return days
    .filter(day =>
      day.targets.some(
        target =>
          target.operationId === operationId && target.status === 'FAILED' && target.retryAllowed
      )
    )
    .map(day => day.date);
}

const SEED_SEASONS: RateGridSeason[] = [
  {
    id: 'season-alta',
    name: 'Temporada alta',
    colour: '#08183a',
    from: dateOf(SEED_MONTH, 16),
    to: dateOf(SEED_MONTH, 20),
    host: { price: 340, minStay: 3, maxStay: null, extraGuestPrice: 15 },
    override: { price: 415, minStay: null, maxStay: null, extraGuestPrice: null },
    version: 1,
    overrideVersion: 0,
  },
  {
    id: 'season-media',
    name: 'Temporada media',
    colour: '#394661',
    from: dateOf(addMonths(SEED_MONTH, 1), 1),
    to: dateOf(addMonths(SEED_MONTH, 1), 10),
    host: { price: 320, minStay: 2, maxStay: null, extraGuestPrice: 10 },
    override: { price: null, minStay: null, maxStay: null, extraGuestPrice: null },
    version: 1,
    overrideVersion: 0,
  },
  {
    ...SEASON_BAJA,
    from: dateOf(SEED_MONTH, seedAnchors(daysInMonth(SEED_MONTH)).seasonFrom),
    to: dateOf(SEED_MONTH, daysInMonth(SEED_MONTH)),
    host: { price: SEASON_PRICE, minStay: 1, maxStay: null, extraGuestPrice: 0 },
    override: { price: null, minStay: null, maxStay: null, extraGuestPrice: null },
    version: 1,
    overrideVersion: 0,
  },
];

const SEASONS = new Map<string, RateGridSeason[]>();

/** The override each season's nights are priced with: the backend applies a saved one later. */
const APPLIED_OVERRIDES = new Map<string, RateGridSeason['override']>();

/** How long the mock backend takes to reprice a season's nights after an override is saved. */
const SEASON_REPRICE_DELAY_MS = 2500;

/** Materialises a rental's seasons once, so an override survives the next read. */
function seasonsOf(rentalId: string): RateGridSeason[] {
  const stored = SEASONS.get(rentalId);
  if (stored) {
    return stored;
  }
  const seasons = SEED_RENTAL_IDS.has(rentalId)
    ? SEED_SEASONS.map(season => ({ ...season, override: { ...season.override } }))
    : [];
  SEASONS.set(rentalId, seasons);
  return seasons;
}

/** Mock: GET /rentals/{rentalId}/rate-grid/seasons. */
export function getRateGridSeasonsMock(rentalId: string): RateGridSeason[] {
  return seasonsOf(rentalId);
}

/** Mock: POST /rentals/{rentalId}/rate-grid/seasons — writes the rental's override for one season. */
export function saveRateGridSeasonMock(
  rentalId: string,
  body: Pick<SeasonWrite, 'seasonId' | 'override'>
): RateGridSeason[] {
  const seasons = seasonsOf(rentalId);
  const season = seasons.find(entry => entry.id === body.seasonId);
  if (season) {
    const key = `${rentalId}:${season.id}`;
    APPLIED_OVERRIDES.set(key, { ...season.override });
    season.override = { ...body.override };
    season.overrideVersion += 1;
    const saved = { ...body.override };
    setTimeout(() => APPLIED_OVERRIDES.set(key, saved), SEASON_REPRICE_DELAY_MS);
  }
  return seasons;
}
